import { ArrowRight, Sparkles, Code2, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import HeroParticleBackground from "./HeroParticleBackground";
import FloatingElements from "./FloatingElements";

const Hero = () => {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0B0C10]"
    >
      {/* Dimmed Particle Background for Hero */}
      <HeroParticleBackground />

      {/* Floating Code Blocks & App Screens */}
      <FloatingElements />

      {/* Animated Grid Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0057FF]/5 via-transparent to-[#00C3FF]/5" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#2C2F36_1px,transparent_1px),linear-gradient(to_bottom,#2C2F36_1px,transparent_1px)] bg-[size:4rem_4rem]" />
      </div>

      {/* Radial Gradient Glow Behind Hero Text (like in image) */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-gradient-radial from-[#0057FF]/20 via-[#00C3FF]/10 to-transparent blur-3xl z-0" />

      {/* Animated Orbs */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#0057FF]/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/3 right-1/4 w-[500px] h-[500px] bg-[#00C3FF]/8 rounded-full blur-3xl animate-float animation-delay-2s animation-duration-8s" />
        <div className="absolute top-1/2 right-1/3 w-72 h-72 bg-[#0057FF]/8 rounded-full blur-3xl animate-float animation-delay-4s animation-duration-10s" />
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 z-10">
        <div className="max-w-6xl mx-auto">
          {/* Main Heading */}
          <div className="text-center space-y-6 animate-fade-in">
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold leading-[1.1] tracking-tight">
              <span className="block text-white">Let's Build</span>
              <span className="block bg-gradient-to-r from-[#0057FF] via-[#00C3FF] to-[#0057FF] bg-clip-text text-transparent animate-gradient drop-shadow-[0_0_30px_rgba(0,87,255,0.5)]">
                Digital Growth.
              </span>
            </h1>
            
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-semibold text-[#B0B3B8]">
              <span className="text-white relative">
                Build, Grow, Automate
                <span className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-[#FFD84C] to-transparent opacity-60"></span>
              </span> — That's Trionix
            </h2>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-10 animate-fade-in animation-delay-200">
            <Button
              size="lg"
              onClick={() => scrollToSection("contact")}
              className="group bg-white text-[#0B0C10] hover:bg-[#FFD84C] hover:text-[#0B0C10] hover:shadow-[0_0_40px_rgba(255,216,76,0.4)] transition-all duration-300 text-base md:text-lg px-8 md:px-10 py-6 md:py-7 rounded-full font-semibold border-0 w-full sm:w-auto min-h-[56px]"
              aria-label="Get started for free"
            >
              Get Started For Free
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>

          {/* Feature Pills */}
          <div className="flex flex-wrap justify-center gap-3 md:gap-4 pt-16 animate-fade-in animation-delay-400">
            {[
              { icon: Code2, label: "Build" },
              { icon: Zap, label: "Grow" },
              { icon: Sparkles, label: "Automate" },
            ].map((feature, index) => (
              <div
                key={index}
                className="flex items-center gap-2 px-5 md:px-6 py-3 rounded-full bg-[#16181D]/50 backdrop-blur-sm border border-[#2C2F36] hover:border-[#0057FF]/50 hover:bg-[#16181D] hover:shadow-[0_0_20px_rgba(0,87,255,0.2)] transition-all duration-300 cursor-pointer group min-h-[44px]"
              >
                <feature.icon className="h-5 w-5 text-[#00C3FF] group-hover:scale-110 transition-transform" />
                <span className="text-sm md:text-base font-medium text-[#B0B3B8]">{feature.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-0 right-0 z-10 flex justify-center">
        <div className="flex flex-col items-center gap-2 animate-bounce">
          <div className="w-[2px] h-12 bg-gradient-to-b from-transparent via-[#00C3FF] to-transparent" />
          <div className="text-xs text-[#8A8D92] font-medium">Scroll Down</div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
